// Device fingerprinting and detection utilities for Mixpanel analytics

interface DeviceInfo {
    deviceId: string;
    deviceType: 'mobile' | 'tablet' | 'desktop';
    deviceModel: string;
    deviceOS: string;
    deviceOSVersion: string;
    browser: string;
    browserVersion: string;
    screenWidth: number;
    screenHeight: number;
    viewportWidth: number;
    viewportHeight: number;
    pixelRatio: number;
    timezone: string;
    language: string;
    userAgent: string;
}

/**
 * Generates a semi-persistent device fingerprint based on browser characteristics
 */
const generateDeviceFingerprint = (): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('Device fingerprint', 2, 2);
    }

    const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        navigator.hardwareConcurrency || 'unknown',
        (navigator as Navigator & ExtendedNavigator).deviceMemory || 'unknown',
        canvas.toDataURL()
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
};

/**
 * Detects device type based on screen size and user agent
 */
const detectDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
    const userAgent = navigator.userAgent.toLowerCase();
    const width = window.innerWidth;

    // Check for mobile indicators in user agent
    if (/iphone|android.*mobile|windows phone|blackberry|bb10|symbian|palm|mobile/i.test(userAgent)) {
        return 'mobile';
    }

    // Check for tablet indicators
    if (/ipad|android(?!.*mobile)|tablet|kindle|silk|playbook/i.test(userAgent)) {
        return 'tablet';
    }

    // Fallback to screen size detection
    if (width <= 768) {
        return 'mobile';
    } else if (width <= 1024) {
        return 'tablet';
    }

    return 'desktop';
};

/**
 * Extracts device model information from user agent
 */
const getDeviceModel = (): string => {
    const userAgent = navigator.userAgent;

    // iPhone models
    if (/iPhone/.test(userAgent)) {
        const match = userAgent.match(/iPhone OS (\d+_\d+)/);
        return match ? `iPhone (iOS ${match[1].replace('_', '.')})` : 'iPhone';
    }

    // iPad models
    if (/iPad/.test(userAgent)) {
        return 'iPad';
    }

    // Android devices
    if (/Android/.test(userAgent)) {
        const match = userAgent.match(/Android ([^;]+)/);
        return match ? `Android ${match[1]}` : 'Android Device';
    }

    // Windows devices
    if (/Windows/.test(userAgent)) {
        if (/Windows Phone/.test(userAgent)) {
            return 'Windows Phone';
        }
        return 'Windows PC';
    }

    // Mac devices
    if (/Macintosh/.test(userAgent)) {
        return 'Mac';
    }

    return 'Unknown Device';
};

/**
 * Detects operating system and version
 */
const getOSInfo = (): { os: string; version: string } => {
    const userAgent = navigator.userAgent;

    // iOS
    if (/iPhone|iPad|iPod/.test(userAgent)) {
        const match = userAgent.match(/OS (\d+_\d+_?\d*)/);
        return {
            os: 'iOS',
            version: match ? match[1].replace(/_/g, '.') : 'Unknown'
        };
    }

    // Android
    if (/Android/.test(userAgent)) {
        const match = userAgent.match(/Android ([^;]+)/);
        return {
            os: 'Android',
            version: match ? match[1] : 'Unknown'
        };
    }

    // Windows
    if (/Windows/.test(userAgent)) {
        if (/Windows NT 10/.test(userAgent)) return { os: 'Windows', version: '10/11' };
        if (/Windows NT 6.3/.test(userAgent)) return { os: 'Windows', version: '8.1' };
        if (/Windows NT 6.2/.test(userAgent)) return { os: 'Windows', version: '8' };
        if (/Windows NT 6.1/.test(userAgent)) return { os: 'Windows', version: '7' };
        return { os: 'Windows', version: 'Unknown' };
    }

    // macOS
    if (/Macintosh/.test(userAgent)) {
        const match = userAgent.match(/Mac OS X ([^)]+)/);
        return {
            os: 'macOS',
            version: match ? match[1].replace(/_/g, '.') : 'Unknown'
        };
    }

    // Linux
    if (/Linux/.test(userAgent)) {
        return { os: 'Linux', version: 'Unknown' };
    }

    return { os: 'Unknown', version: 'Unknown' };
};

/**
 * Detects browser and version
 */
const getBrowserInfo = (): { browser: string; version: string } => {
    const userAgent = navigator.userAgent;

    // Chrome
    if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) {
        const match = userAgent.match(/Chrome\/([^\s]+)/);
        return {
            browser: 'Chrome',
            version: match ? match[1] : 'Unknown'
        };
    }

    // Edge
    if (/Edg/.test(userAgent)) {
        const match = userAgent.match(/Edg\/([^\s]+)/);
        return {
            browser: 'Edge',
            version: match ? match[1] : 'Unknown'
        };
    }

    // Firefox
    if (/Firefox/.test(userAgent)) {
        const match = userAgent.match(/Firefox\/([^\s]+)/);
        return {
            browser: 'Firefox',
            version: match ? match[1] : 'Unknown'
        };
    }

    // Safari
    if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
        const match = userAgent.match(/Version\/([^\s]+)/);
        return {
            browser: 'Safari',
            version: match ? match[1] : 'Unknown'
        };
    }

    return { browser: 'Unknown', version: 'Unknown' };
};

/**
 * Gets current viewport dimensions
 */
const getViewportDimensions = (): { width: number; height: number } => {
    return {
        width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
        height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    };
};

/**
 * Main function to collect all device information
 */
export const getDeviceInfo = (): DeviceInfo => {
    const osInfo = getOSInfo();
    const browserInfo = getBrowserInfo();
    const viewport = getViewportDimensions();

    return {
        deviceId: generateDeviceFingerprint(),
        deviceType: detectDeviceType(),
        deviceModel: getDeviceModel(),
        deviceOS: osInfo.os,
        deviceOSVersion: osInfo.version,
        browser: browserInfo.browser,
        browserVersion: browserInfo.version,
        screenWidth: screen.width,
        screenHeight: screen.height,
        viewportWidth: viewport.width,
        viewportHeight: viewport.height,
        pixelRatio: window.devicePixelRatio || 1,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        userAgent: navigator.userAgent
    };
};

/**
 * Legacy function for backward compatibility
 */
export const deviceInfo = (): DeviceInfo => {
    return getDeviceInfo();
};

/**
 * Get a simplified device info object for basic tracking
 */
export const getBasicDeviceInfo = () => {
    const fullInfo = getDeviceInfo();
    return {
        deviceId: fullInfo.deviceId,
        deviceType: fullInfo.deviceType,
        deviceOS: fullInfo.deviceOS,
        browser: fullInfo.browser,
        screenSize: `${fullInfo.screenWidth}x${fullInfo.screenHeight}`,
        language: fullInfo.language
    };
};

/**
 * Get device performance metrics
 */
/**
 * Extended Navigator interface for device memory and connection info
 */
interface ExtendedNavigator {
    deviceMemory?: number;
    connection?: {
        effectiveType?: string;
        downlink?: number;
        saveData?: boolean;
    };
}

/**
 * Get device performance metrics
 */
export const getDevicePerformanceInfo = () => {
    const nav = navigator as Navigator & ExtendedNavigator;
    return {
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
        deviceMemory: nav.deviceMemory || 'unknown',
        connectionType: nav.connection?.effectiveType || 'unknown',
        connectionDownlink: nav.connection?.downlink || 'unknown'
    };
};